import {
  appBaseDomain,
  publicWebBaseDomain,
  rootDomain,
} from '../config/deployment';

export { appBaseDomain, publicWebBaseDomain } from '../config/deployment';

const RESERVED_SLUGS = new Set([
  'www',
  'api',
  'app',
  'admin',
  'superadmin',
  'super-admin',
  'mail',
  'ftp',
  'localhost',
  'test',
  'dev',
  'staging',
  'portal',
  'static',
  'assets',
  'cdn',
  'docs',
  'status',
  'support',
  'help',
  'billing',
  'auth',
  'login',
  'signup',
  'platform',
  'origin-api',
  'origin',
  'sites',
]);

function reservedSlugs(): Set<string> {
  const out = new Set(RESERVED_SLUGS);
  const brand = rootDomain().split('.')[0]?.trim();
  if (brand) out.add(brand);
  return out;
}

export function normalizeTenantSlug(input: string): string {
  const trimmed = input.trim().toLowerCase();
  let out = '';
  let prevHyphen = false;
  for (const ch of trimmed) {
    if ((ch >= 'a' && ch <= 'z') || (ch >= '0' && ch <= '9')) {
      out += ch;
      prevHyphen = false;
    } else if (ch === '-' || ch === '_' || ch === ' ') {
      if (out && !prevHyphen) {
        out += '-';
        prevHyphen = true;
      }
    }
  }
  return out.replace(/^-+|-+$/g, '');
}

export function validateTenantSlug(input: string): string | null {
  const slug = normalizeTenantSlug(input);
  if (!slug) return 'Tenant URL slug is required.';
  if (slug.length < 3) return 'Tenant URL slug must be at least 3 characters.';
  if (slug.length > 63) return 'Tenant URL slug must be at most 63 characters.';
  if (reservedSlugs().has(slug)) return 'This tenant URL slug is reserved. Choose another.';
  if (!/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(slug)) {
    return 'Tenant URL slug may only contain letters, numbers, and hyphens.';
  }
  return null;
}

export function tenantPublicUrl(slug: string): string {
  const normalized = normalizeTenantSlug(slug);
  if (!normalized) return '';
  return `https://${normalized}.${appBaseDomain()}`;
}

/** Public brochure URL for Generic_web_ui (`{slug}.{public_web_domain}`). */
export function tenantBrochureUrl(slug: string): string {
  const normalized = normalizeTenantSlug(slug);
  const base = publicWebBaseDomain();
  if (!normalized || !base) return '';
  return `https://${normalized}.${base}`;
}

/** Extract `{slug}` from `{slug}.{appBaseDomain}` (or current host when it matches). */
export function slugFromHostname(hostname: string, baseDomain = appBaseDomain()): string | null {
  const host = hostname.trim().toLowerCase().split(':')[0]?.replace(/\.$/, '') || '';
  const base = baseDomain.trim().toLowerCase().replace(/^\.+|\.+$/g, '');
  if (!host || !base || host === base) return null;
  const suffix = `.${base}`;
  if (!host.endsWith(suffix)) return null;
  const slug = host.slice(0, -suffix.length);
  if (!slug || slug.includes('.')) return null;
  const normalized = normalizeTenantSlug(slug);
  return validateTenantSlug(normalized) ? null : normalized;
}

/** True when the hostname resolves to a tenant institution portal (not apex/www/local). */
export function isTenantHost(hostname?: string): boolean {
  const host =
    hostname ??
    (typeof window !== 'undefined' ? window.location.hostname : '');
  return Boolean(slugFromHostname(host));
}

/**
 * Origin for a tenant's ERP portal, preserving local protocol/port when developing
 * on localhost (`http://{slug}.localhost:3000`).
 */
export function tenantPortalOrigin(slug: string): string {
  const normalized = normalizeTenantSlug(slug);
  if (!normalized) return '';
  if (typeof window !== 'undefined') {
    const { protocol, port, hostname } = window.location;
    const host = hostname.toLowerCase();
    if (host === 'localhost' || host === '127.0.0.1' || host.endsWith('.localhost')) {
      const portPart = port ? `:${port}` : '';
      return `${protocol}//${normalized}.localhost${portPart}`;
    }
  }
  return tenantPublicUrl(normalized);
}
